import { useEffect, useRef } from 'react'
import { X, ShoppingCart, Minus, Plus, Trash2, TrendingUp, Users, Globe, Search, BarChart3 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useCart } from '../contexts/CartContext'
import { useLanguage } from '../contexts/LanguageContext'

interface CartModalProps {
  isOpen: boolean
  onClose: () => void
}

const CartModal = ({ isOpen, onClose }: CartModalProps) => {
  const navigate = useNavigate()
  const { items, removeFromCart, updateQuantity, getTotalPrice, clearCart } = useCart()
  const { language } = useLanguage()
  const previousOverflow = useRef<string | null>(null)

  const total = getTotalPrice()
  const isValid = items.length > 0

  const translations = {
    tr: {
      title: 'Sepetim',
      empty: 'Sepetiniz boş',
      total: 'Toplam',
      checkout: 'Ödemeye Geç',
      remove: 'Kaldır',
      clearAll: 'Sepeti Temizle',
      hours: 'saat',
      cancel: 'İptal'
    },
    en: {
      title: 'My Cart',
      empty: 'Your cart is empty',
      total: 'Total',
      checkout: 'Checkout',
      remove: 'Remove',
      clearAll: 'Clear Cart',
      hours: 'hours',
      cancel: 'Cancel'
    },
    ru: {
      title: 'Моя корзина',
      empty: 'Ваша корзина пуста',
      total: 'Итого',
      checkout: 'Оформить заказ',
      remove: 'Удалить',
      clearAll: 'Очистить корзину',
      hours: 'часов',
      cancel: 'Отмена'
    }
  }

  const t = translations[language as 'tr' | 'en' | 'ru']

  const getIcon = (iconName?: string) => {
    const icons: Record<string, any> = {
      TrendingUp,
      Users,
      Search,
      Globe,
      ShoppingCart,
      BarChart3
    }
    return icons[iconName || 'ShoppingCart'] || ShoppingCart
  }

  useEffect(() => {
    if (isOpen) {
      previousOverflow.current = document.body.style.overflow || ''
      document.body.style.overflow = 'hidden'
    } else if (previousOverflow.current !== null) {
      document.body.style.overflow = previousOverflow.current
      previousOverflow.current = null
    }

    return () => {
      if (previousOverflow.current !== null) {
        document.body.style.overflow = previousOverflow.current
        previousOverflow.current = null
      } else if (!isOpen) {
        document.body.style.overflow = ''
      }
    }
  }, [isOpen])

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-[99] bg-black/50 backdrop-blur-sm transition-opacity duration-300"
          onClick={onClose}
        />
      )}
      
      {/* Slide-in Cart Panel */}
      <div 
        className={`fixed top-0 right-0 z-[100] h-full w-full sm:w-96 md:max-w-md bg-white shadow-2xl transform transition-transform duration-300 ease-in-out flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        data-cart-modal={isOpen ? 'open' : 'closed'}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 bg-white sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
              <ShoppingCart className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{t.title}</h2>
              {items.length > 0 && (
                <p className="text-xs text-gray-500">{items.length} {language === 'tr' ? 'ürün' : 'item'}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors group"
          >
            <X className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50/50">
          {items.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <ShoppingCart className="w-10 h-10 text-gray-300" />
              </div>
              <p className="text-gray-500 text-lg font-medium mb-2">{t.empty}</p>
              <p className="text-sm text-gray-400">{language === 'tr' ? 'Sepetinize ürün ekleyin' : 'Add items to your cart'}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((item) => {
                const IconComponent = getIcon(item.icon)
                return (
                <div key={item.id} className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200 group">
                  <div className="flex items-start gap-4">
                    {/* Item Icon */}
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0 shadow-md">
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    
                    {/* Item Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-1 text-[15px] leading-tight">
                            {language === 'tr' ? item.name : item.nameEn}
                          </h3>
                          <p className="text-xs text-gray-500">
                            {item.hours} {t.hours} × {item.pricePerHour.toLocaleString('tr-TR')} ₺/{language === 'tr' ? 'saat' : 'hour'}
                          </p>
                        </div>
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="p-1.5 hover:bg-red-50 text-gray-400 hover:text-red-600 rounded-lg transition-colors flex-shrink-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      
                      {/* Quantity Controls and Price */}
                      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-600 hover:text-gray-900 transition-colors"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="w-8 text-center font-semibold text-gray-900 text-sm">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-600 hover:text-gray-900 transition-colors"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-gray-900">
                            {(item.totalPrice * item.quantity).toLocaleString('tr-TR')} ₺
                          </div>
                          {item.quantity > 1 && (
                            <div className="text-xs text-gray-500">
                              {item.totalPrice.toLocaleString('tr-TR')} ₺ {language === 'tr' ? 'adet' : 'each'}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-gray-200 bg-white p-6 space-y-4 sticky bottom-0">
            {/* Summary */}
            <div className="flex items-center justify-between pb-4 border-b border-gray-100">
              <div>
                <span className="text-sm text-gray-600">{t.total}</span>
                <p className="text-xs text-gray-400 mt-0.5">
                  {items.reduce((sum, item) => sum + item.quantity, 0)} {language === 'tr' ? 'ürün' : 'items'}
                </p>
              </div>
              <div className="text-right">
                <span className="text-3xl font-bold text-gray-900">
                  {total.toLocaleString('tr-TR')} ₺
                </span>
                <p className="text-xs text-gray-500 mt-0.5">
                  {language === 'tr' ? 'KDV dahil' : 'VAT included'}
                </p>
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <button
                onClick={clearCart}
                className="px-4 py-3 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all font-medium text-sm"
              >
                {t.clearAll}
              </button>
              <button
                onClick={() => {
                  onClose()
                  navigate('/checkout')
                }}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all font-semibold text-sm shadow-lg shadow-blue-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!isValid}
              >
                {t.checkout}
              </button>
            </div>
            
            {/* Trust Badge */}
            <div className="pt-2 text-center text-xs text-gray-500">
              {language === 'tr'
                ? 'Ödemeyi tamamlamak için havale/EFT bilgileri ödeme sayfasında paylaşılacaktır.'
                : language === 'en'
                ? 'Bank transfer details will be shared on the checkout page.'
                : 'Реквизиты для банковского перевода будут указаны на странице оплаты.'}
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default CartModal















